import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Eye, Bell, LineChart, Wallet, Star, CheckCircle } from 'lucide-react';

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  target?: string; // CSS selector for highlighting
  position: 'center' | 'left' | 'right';
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to my investments!',
    description: 'Let us show you around the platform. This quick tour will help you get started with tracking your investments across JSE, US markets, crypto, and forex.',
    icon: <Sparkles className="h-8 w-8 text-yellow-500" />,
    position: 'center',
  },
  {
    id: 'dashboard',
    title: 'Dashboard Overview',
    description: 'Your dashboard gives you a quick overview of market performance, your watchlist, and portfolio summary. Check here daily to stay on top of the markets.',
    icon: <Eye className="h-8 w-8 text-blue-500" />,
    position: 'center',
  },
  {
    id: 'watchlist',
    title: 'Create Watchlists',
    description: 'Track your favorite stocks and assets by creating watchlists. Add symbols from JSE, US markets, cryptocurrencies, and forex pairs all in one place.',
    icon: <Star className="h-8 w-8 text-amber-500" />,
    position: 'center',
  },
  {
    id: 'portfolio',
    title: 'Track Your Portfolio',
    description: 'Add your holdings to track your investment performance in real-time. See your gains, losses, and overall portfolio value at a glance.',
    icon: <Wallet className="h-8 w-8 text-green-500" />,
    position: 'center',
  },
  {
    id: 'screener',
    title: 'Stock Screener',
    description: 'Use our powerful screener to find stocks based on technical signals. Filter by buy/sell signals, strength, and confidence levels.',
    icon: <LineChart className="h-8 w-8 text-purple-500" />,
    position: 'center',
  },
  {
    id: 'alerts',
    title: 'Set Price Alerts',
    description: 'Never miss an opportunity! Set price alerts for any symbol and get notified when prices reach your targets. You can also set technical indicator alerts for RSI, MACD, and more.',
    icon: <Bell className="h-8 w-8 text-red-500" />,
    position: 'center',
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'You\'re ready to start tracking your investments. Explore the platform and don\'t hesitate to check out the Pro subscription for access to global markets, AI analysis, and more!',
    icon: <CheckCircle className="h-8 w-8 text-emerald-500" />,
    position: 'center',
  },
];

interface OnboardingTourProps {
  onComplete: () => void;
  isOpen: boolean;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete, isOpen }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setIsAnimating(false);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!isOpen) return null;

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all duration-200 ${
          isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
        }`}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close tour"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 shadow-inner">
              {step.icon}
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-4">
            {tourSteps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  idx === currentStep
                    ? 'w-6 bg-blue-500'
                    : idx < currentStep
                    ? 'bg-blue-300 dark:bg-blue-700'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-label={`Go to step ${idx + 1}`}
              />
            ))}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {step.title}
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            {step.description}
          </p>

          {/* Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${
                isFirstStep
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>

            <button
              onClick={handleSkip}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
            >
              Skip tour
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
